import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { hydraApi, kratosPublicApi, sessionApi } from '../api/client'
import {
  bridgeUrl, browserRedirectUrl, classifyFlowFailure, continuationUrl, flowContext,
  flowReturnTo, flowUrl, isKratosFlow, record,
  prefillMigrationEmail,
  submissionTransition,
  type FlowContext, type FlowKind, type FlowSubmission, type KratosFlow,
} from '../api/flows'

type LoadedFlow = { data: unknown } | { redirect: string }
type FlowScope = { active: boolean; controller: AbortController }

async function loadFlow(kind: FlowKind, id: string | null, context: FlowContext, signal: AbortSignal): Promise<LoadedFlow> {
  const withEmail = async (response: { data: unknown }): Promise<LoadedFlow> => {
    if (!isKratosFlow(response.data)) return response
    const restored = flowContext('', response.data, kind, window.location.origin)
    const challenge = context.loginChallenge || restored.loginChallenge
    if (!challenge || !['login', 'registration'].includes(kind)) return response
    const hint = await hydraApi.loginContext(challenge, signal)
    return { data: prefillMigrationEmail(response.data, kind, hint.data.email) }
  }
  if (id) return withEmail(await kratosPublicApi.getFlow(kind, id, signal))
  if (kind === 'login' && context.loginChallenge && !context.refresh) {
    // The bridge decides whether the existing session meets prompt/max_age.
    // A frontend session check alone cannot decide whether SSO can continue.
    try {
      const response = await hydraApi.acceptLogin(context.loginChallenge, true, signal)
      return { redirect: response.data.redirect_to }
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 401) throw error
      const body = record(error.response.data)
      if (typeof body.redirect_to === 'string') return { redirect: body.redirect_to }
      if (body.error !== 'not authenticated') throw error
    }
  }
  if (signal.aborted) throw new Error('The previous authentication request was cancelled.')
  return withEmail(await kratosPublicApi.initializeFlow(kind, flowReturnTo(context, window.location.origin, kind), context.refresh, context.aal, signal))
}

async function existingSessionRedirect(context: FlowContext, signal: AbortSignal): Promise<string> {
  if (context.loginChallenge) {
    return bridgeUrl(context.loginChallenge, window.location.origin)
  }
  // Kratos also reports pending registration cookies as existing sessions.
  // Application session status decides whether to resume email verification.
  const session = await sessionApi.checkSession(signal)
  if (!session.data.active && session.data.verification_required === true) {
    return flowUrl('verification', context)
  }
  if (!session.data.active) throw new Error('The session is no longer active.')
  if (context.returnTo) {
    // This value may come directly from a query parameter. Let Kratos apply its
    // return URL allowlist, including when it reports an existing session.
    const url = new URL('/kratos/self-service/login/browser', window.location.origin)
    url.searchParams.set('return_to', context.returnTo)
    return url.href
  }
  return '/'
}

export function useKratosFlow(kind: FlowKind) {
  const location = useLocation()
  const navigate = useNavigate()
  const query = location.search
  const path = location.pathname
  const [flow, setFlow] = useState<KratosFlow | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  const [retry, setRetry] = useState(0)
  const pending = useRef<{ key: string; promise: Promise<LoadedFlow>; controller: AbortController }>()
  const normalizedLocation = useRef<string | null>(null)
  const submitting = useRef(false)
  const lifecycle = useRef<FlowScope | null>(null)

  const applyFlow = useCallback((nextFlow: KratosFlow) => {
    setFlow(nextFlow)
    setVersion(value => value + 1)
    const nextUrl = flowUrl(kind, flowContext(query, nextFlow, kind, window.location.origin), nextFlow.id, path)
    if (`${path}${query}` !== nextUrl) {
      // This URL update only records the flow we already received. Reuse that
      // response for this exact destination instead of fetching it a second time.
      normalizedLocation.current = `${kind}:${nextUrl}:${retry}`
      navigate(nextUrl, { replace: true })
    }
  }, [kind, path, query, retry, navigate])

  const handleFailure = useCallback(async (failure: unknown, previousFlow?: KratosFlow | null, scope: FlowScope | null = lifecycle.current) => {
    if (!scope?.active) return
    const context = flowContext(query, previousFlow, kind, window.location.origin)
    const response = axios.isAxiosError(failure) ? failure.response : undefined
    const body = response?.data
    let redirect: string | undefined
    try {
      redirect = continuationUrl(body, context, window.location.origin)
    } catch {
      setFlow(null)
      setError('The authentication service returned an invalid redirect. Please try again.')
      return
    }
    if (redirect) {
      window.location.assign(redirect)
      return
    }
    const result = classifyFlowFailure(response?.status, body)
    if (result.kind === 'flow') {
      setError(null)
      applyFlow(result.flow)
    } else if (result.kind === 'session') {
      try {
        const target = await existingSessionRedirect(context, scope.controller.signal)
        if (scope.active) window.location.assign(target)
      } catch {
        if (scope.active) {
          setFlow(null)
          setError('Unable to confirm your session. Please try again.')
        }
      }
    } else if (result.kind === 'reauthenticate') {
      // Reauthentication for changing a password must return to settings first.
      // Keep the SSO challenge in that nested return URL, not the login request.
      const returnTo = new URL(flowUrl(kind, context, previousFlow?.id, path), window.location.origin).href
      const loginUrl = flowUrl('login', { returnTo, refresh: true,
        aal: record(record(body).error).id === 'session_aal2_required' ? 'aal2' : undefined })
      navigate(loginUrl, { replace: true })
    } else if (result.kind === 'restart') {
      setError('This form has expired. A new form is ready for you to try again.')
      setFlow(null)
      const oldId = new URLSearchParams(query).get('flow')
      const nextContext = { ...context, returnTo: result.returnTo || context.returnTo }
      if (oldId || previousFlow) {
        navigate(flowUrl(kind, nextContext, result.id !== oldId ? result.id : undefined, path), { replace: true })
      } else {
        setError('Unable to create a new form. Please try again.')
      }
    } else {
      setError(result.message)
    }
  }, [kind, query, path, navigate, applyFlow])

  useEffect(() => {
    const scope = { active: true, controller: new AbortController() }
    lifecycle.current = scope
    submitting.current = false
    setBusy(false)
    const key = `${kind}:${path}${query}:${retry}`
    const reuseFlow = normalizedLocation.current === key
    if (reuseFlow) {
      // Keep the displayed nodes/messages through our own replace navigation.
      // Do not retain the old initialization promise: returning to its URL is
      // an external navigation and must create a fresh browser flow.
      pending.current = undefined
      setLoading(false)
    } else {
      normalizedLocation.current = null
      setFlow(null)
      setLoading(true)
      setError(null)
      setSuccess(null)
      if (pending.current?.key !== key) {
        const controller = new AbortController()
        pending.current = {
          key,
          controller,
          promise: loadFlow(kind, new URLSearchParams(query).get('flow'), flowContext(query, undefined, kind, window.location.origin), controller.signal),
        }
      }
    }
    const request = pending.current
    // Reuse initialization during React StrictMode's effect replay so that two
    // browser flows cannot race to replace the same CSRF cookie.
    request?.promise.then(result => {
      if (!scope.active) return
      if ('redirect' in result) {
        window.location.assign(browserRedirectUrl(result.redirect, window.location.origin))
      } else if (isKratosFlow(result.data)) {
        const context = flowContext(query, result.data, kind, window.location.origin)
        const transition = submissionTransition(kind, result.data, context, window.location.origin)
        if (transition.kind === 'redirect') window.location.assign(transition.url)
        else applyFlow(result.data)
      } else {
        const redirect = continuationUrl(result.data, flowContext(query, undefined, kind, window.location.origin), window.location.origin)
        if (redirect) window.location.assign(redirect)
        else throw new Error('The authentication service returned an invalid form.')
      }
    }).catch(failure => {
      if (scope.active) return handleFailure(failure, undefined, scope)
    }).finally(() => {
      if (scope.active) setLoading(false)
    })
    return () => {
      scope.active = false
      scope.controller.abort()
      // StrictMode immediately replays this effect with the same pending
      // initialization. Cancel only after that replay can reclaim the request.
      queueMicrotask(() => {
        if (request && (!lifecycle.current?.active || pending.current !== request)) request.controller.abort()
      })
    }
  }, [kind, path, query, retry, applyFlow, handleFailure])

  const submit = async (submission: FlowSubmission) => {
    const scope = lifecycle.current
    if (!flow || submitting.current || !scope?.active) return
    submitting.current = true
    setBusy(true)
    setError(null)
    setSuccess(null)
    const context = flowContext(query, flow, kind, window.location.origin)
    let preparing = typeof submission === 'function'
    let submittedData: Record<string, string> | undefined
    try {
      // Acquire the same mutation lock before opening the authenticator. A
      // password submit cannot race a pending Passkey prompt, and its result
      // cannot submit a stale flow after navigation or component teardown.
      const data = typeof submission === 'function' ? await submission(scope.controller.signal) : submission
      if (!scope.active) return
      preparing = false
      submittedData = data
      const response = await kratosPublicApi.submitFlow(flow.ui.action, data, scope.controller.signal)
      if (!scope.active) return
      const transition = submissionTransition(kind, response.data, context, window.location.origin)
      if (transition.kind === 'redirect') {
        window.location.assign(transition.url)
        return
      }
      if (transition.kind === 'flow') {
        applyFlow(transition.flow)
        if (transition.saved && !transition.flow.ui.messages?.some(message => message.type === 'success')) {
          setSuccess(data.method === 'password' ? 'Your password has been updated.' : 'Your account has been updated.')
        }
        return
      }
      if (transition.kind === 'confirm-session') {
        const session = await sessionApi.checkSession(scope.controller.signal)
        if (!scope.active) return
        if (!session.data.active) throw new Error('The new session could not be confirmed.')
        const destination = context.loginChallenge ? bridgeUrl(context.loginChallenge, window.location.origin) :
          flow.return_to ? browserRedirectUrl(flow.return_to, window.location.origin) : '/'
        window.location.assign(destination)
      } else if (kind === 'settings') {
        const updated = await kratosPublicApi.getFlow(kind, flow.id, scope.controller.signal)
        if (!scope.active) return
        if (!isKratosFlow(updated.data)) throw new Error('The authentication service returned an invalid form.')
        applyFlow(updated.data)
        if (!updated.data.ui.messages?.some(message => message.type === 'success')) setSuccess('Your account has been updated.')
      } else {
        const updated = await kratosPublicApi.getFlow(kind, flow.id, scope.controller.signal)
        if (!scope.active) return
        if (!isKratosFlow(updated.data)) throw new Error('The authentication service returned an invalid form.')
        applyFlow(updated.data)
      }
    } catch (failure) {
      if (scope.active) {
        const response = axios.isAxiosError(failure) ? failure.response : undefined
        const failedFlow = isKratosFlow(response?.data) ? response.data : null
        const ambiguousResponse = !response || response.status >= 500
        if (kind === 'settings' && submittedData?.passkey_settings_register && (failedFlow || ambiguousResponse)) {
          // v26 consumes the registration challenge before its policy hook.
          // A denied/ambiguous POST must obtain a new flow, not replay that
          // challenge. Reload authoritative credentials without resubmission.
          try {
            const replacement = await kratosPublicApi.initializeFlow('settings', flowReturnTo(context, window.location.origin, 'settings'), false, undefined, scope.controller.signal)
            if (!scope.active) return
            if (!isKratosFlow(replacement.data)) throw new Error('Invalid settings flow')
            const messages = failedFlow?.ui.messages
            applyFlow(messages?.length ? { ...replacement.data, ui: { ...replacement.data.ui, messages } } : replacement.data)
            if (!messages?.length) setError('Unable to confirm this Passkey update. Check your Passkeys before trying again.')
          } catch (refreshFailure) {
            if (scope.active) {
              setFlow(null)
              await handleFailure(refreshFailure, flow, scope)
            }
          }
        } else if (preparing && failure instanceof Error) setError(failure.message)
        else await handleFailure(failure, flow, scope)
      }
    } finally {
      if (scope.active) {
        submitting.current = false
        setBusy(false)
      }
    }
  }

  const restart = () => {
    setFlow(null)
    setError(null)
    const target = flowUrl(kind, flowContext(query, flow, kind, window.location.origin), undefined, path)
    if (target === `${path}${query}`) setRetry(value => value + 1)
    else navigate(target, { replace: true })
  }

  return { flow, loading, busy, error, success, version, context: flowContext(query, flow, kind, window.location.origin), submit, restart }
}
